/**
 * Picks the Document Smart Text token highlight for a locked textbox based on
 * the page background it sits on.
 *
 * The default highlight is a translucent brand-blue wash (set in CSS). That
 * reads well on white/light-neutral pages, but a low-opacity tint barely
 * shifts the colour of a saturated or dark background (blue, green, red, …),
 * so the assigned values disappear.
 *
 * On anything other than a near-white page we therefore swap to a neutral
 * wash chosen by the background's lightness — white on dark backgrounds,
 * black on light ones. A lightness shift is perceptible across every hue, so
 * the highlight stays visible regardless of the background colour.
 *
 * Returns `null` when the default brand-blue wash is appropriate (near-white
 * pages) or the background can't be resolved (gradients/images), letting the
 * CSS fallback apply.
 */

interface Rgb {
  r: number;
  g: number;
  b: number;
}

export interface DocTokenHighlight {
  base: string;
  hover: string;
}

// Backgrounds whose darkest channel is at/above this are near-white /
// light-neutral, where the on-brand blue wash still reads well. Tunable.
const NEAR_WHITE_MIN = 210;

// Below this relative luminance the background is "dark" and wants a light
// (white) wash; at/above it the background is "light" and wants a dark wash.
const LIGHTNESS_SPLIT = 0.5;

const WHITE_WASH: DocTokenHighlight = {
  base: "rgba(255, 255, 255, 0.32)",
  hover: "rgba(255, 255, 255, 0.45)"
};

const DARK_WASH: DocTokenHighlight = {
  base: "rgba(0, 0, 0, 0.2)",
  hover: "rgba(0, 0, 0, 0.28)"
};

const parseColorToRgb = (color: string): Rgb | null => {
  const c = color.trim().toLowerCase();
  if (c === "transparent") return null;

  const hexMatch = c.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/);
  if (hexMatch?.[1]) {
    const raw = hexMatch[1];
    const h =
      raw.length === 3
        ? raw
            .split("")
            .map(ch => ch + ch)
            .join("")
        : raw;
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16)
    };
  }

  const rgbMatch = c.match(/^rgba?\(([^)]+)\)$/);
  if (rgbMatch?.[1]) {
    const [r, g, b] = rgbMatch[1].split(",").map(p => parseFloat(p));
    if (
      r !== undefined &&
      g !== undefined &&
      b !== undefined &&
      !Number.isNaN(r) &&
      !Number.isNaN(g) &&
      !Number.isNaN(b)
    ) {
      return { r, g, b };
    }
  }

  return null;
};

const channelLuminance = (value: number): number => {
  const s = value / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
};

const relativeLuminance = ({ r, g, b }: Rgb): number =>
  0.2126 * channelLuminance(r) +
  0.7152 * channelLuminance(g) +
  0.0722 * channelLuminance(b);

export const getDocTokenHighlight = (
  pageBackgroundColor: string | null | undefined
): DocTokenHighlight | null => {
  if (!pageBackgroundColor) return null;

  const bg = parseColorToRgb(pageBackgroundColor);
  if (!bg) return null;

  // Near-white / light-neutral page: the default brand-blue wash reads fine.
  if (Math.min(bg.r, bg.g, bg.b) >= NEAR_WHITE_MIN) return null;

  // Any other background: a neutral wash that contrasts with its lightness.
  return relativeLuminance(bg) < LIGHTNESS_SPLIT ? WHITE_WASH : DARK_WASH;
};
